import React from 'react';
import { View, Text, StyleSheet, ScrollView, Pressable } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import AttendanceItem from './AttendanceItem';
import { isDateInFuture, normalizeAttendanceRecord } from './utils';

interface AttendanceTabProps {
    date: string;
    registeredUsers: any[];
    sessionSummary: any;
    isCoach: boolean;
    currentUser: any;
    onToggleAttendance: (userId: string, date: string, type: 'checkin' | 'checkout') => void;
    onClassCommentPress: (date: string) => void;
    onClassPhotoPress: (date: string) => void;
    onStudentCommentPress: (userId: string, date: string) => void;
    onStudentPhotoPress: (userId: string, date: string) => void;
}

const AttendanceTab: React.FC<AttendanceTabProps> = ({
    date,
    registeredUsers,
    sessionSummary,
    isCoach,
    currentUser,
    onToggleAttendance,
    onClassCommentPress,
    onClassPhotoPress,
    onStudentCommentPress,
    onStudentPhotoPress,
}) => {
    const isFutureDate = isDateInFuture(date);
    const displayUsers = isCoach 
        ? registeredUsers 
        : registeredUsers.filter(user => user.parentId === currentUser?.userId);
    const checkedInCount = displayUsers.filter((user) => normalizeAttendanceRecord(sessionSummary.attendance?.[date]?.[user.userId]).checkedIn).length;
    const checkedOutCount = displayUsers.filter((user) => normalizeAttendanceRecord(sessionSummary.attendance?.[date]?.[user.userId]).checkedOut).length;

    return (
        <ScrollView style={styles.tabContent} showsVerticalScrollIndicator={false}>
            <View style={styles.attendanceHeader}>
                <View style={styles.attendanceHeaderLeft}>
                    <Text style={styles.attendanceDate}>
                        {new Date(date).toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                        })}
                    </Text>
                    <Text style={styles.attendanceCount}>
                        {checkedInCount} checked in • {checkedOutCount} checked out
                    </Text>
                </View>
                
                <View style={styles.classActions}>
                    <Pressable 
                        style={styles.actionButton} 
                        onPress={() => onClassCommentPress(date)}
                    >
                        <MaterialCommunityIcons name="comment-text-outline" size={16} color="#666" />
                    </Pressable>
                    <Pressable 
                        style={styles.actionButton} 
                        onPress={() => onClassPhotoPress(date)}
                    >
                        <MaterialCommunityIcons name="camera-outline" size={16} color="#666" />
                    </Pressable>
                </View>
            </View>

            {displayUsers.length > 0 ? (
                <View style={styles.attendanceList}>
                    {displayUsers.map((user) => (
                        <AttendanceItem
                            key={user.userId}
                            user={user}
                            date={date}
                            attendance={normalizeAttendanceRecord(sessionSummary.attendance?.[date]?.[user.userId])}
                            isCoach={isCoach}
                            isFutureDate={isFutureDate}
                            onToggleAttendance={onToggleAttendance}
                            onCommentPress={onStudentCommentPress}
                            onPhotoPress={onStudentPhotoPress}
                        />
                    ))}
                </View>
            ) : (
                <View style={styles.emptyState}>
                    <MaterialCommunityIcons name="clipboard-check-outline" size={48} color="#ccc" />
                    <Text style={styles.emptyStateText}>No campers to track attendance</Text>
                </View>
            )}
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    tabContent: {
        flex: 1,
        padding: 24,
    },
    attendanceHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 24,
        padding: 20,
        backgroundColor: '#f8f9fa',
        borderRadius: 16,
        borderWidth: 1,
        borderColor: '#e9ecef',
    },
    attendanceHeaderLeft: {
        flex: 1,
    },
    attendanceDate: {
        fontSize: 20,
        fontWeight: '700',
        color: '#1a1a1a',
        marginBottom: 8,
    },
    attendanceCount: {
        fontSize: 16,
        color: '#666',
        fontWeight: '500',
    },
    classActions: {
        flexDirection: 'row',
        gap: 8,
    },
    actionButton: {
        padding: 8,
        borderRadius: 6,
        backgroundColor: '#f8f9fa',
        borderWidth: 1,
        borderColor: '#e9ecef',
    },
    attendanceList: {
        gap: 12,
    },
    emptyState: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 60,
    },
    emptyStateText: {
        fontSize: 16,
        color: '#999',
        marginTop: 16,
        textAlign: 'center',
    },
});

export default AttendanceTab;

